import {
  clientSchema,
  logout as createLogout,
  saveAccount,
  setCurrentClientId,
  ThunkDispatch,
} from '../store';
import { Store } from 'redux';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Account,
  LogoutOptions,
  queryCreate,
  queryGet,
  useLogout as useApiLogout,
  Client,
  queryUpdate,
} from '@app/api';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { getOneSuccessAction } from '../store/modules/actions';
import { normalize } from 'normalizr';
import { useCallback } from 'react';
import { useIdentifyAccount, useUpdateIdentify } from '@app/analytics';

export interface ClientCreation {
  account: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    locale?: string;
  };
  organization: {
    name: string;
    billingMethod?: string;
  };
  phoneNumber: string;
  jobTitle: string;
  promoCode?: string;
  altcha: string;
}

export interface ClientUpdate {
  account: {
    firstName: string;
    lastName: string;
    email: string;
    locale?: string;
    timezone?: string;
  };
  phoneNumber: string;
}

export interface ClientWithInvitationCreation {
  account: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    locale?: string;
  };
  phoneNumber: string;
  invitationToken: string;
  jobTitle: string;
}

let store: Store<any, any> | undefined = undefined;

export const useLogout = (options?: Omit<LogoutOptions, 'onLogout'>) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const onLogout = useCallback(() => dispatch(createLogout()), [dispatch]);
  return useApiLogout({ ...options, onLogout });
};

export const useMe = (allowAnonymous?: boolean) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { mutate: logout } = useLogout({ keepCurrentPage: true });
  const identity = useIdentifyAccount();

  return useQuery<Account, AxiosError>(
    ['auth', 'me'],
    queryGet('/v2/auth/me'),
    {
      staleTime: 1000 * 60, // 1 min
      onSuccess: (data) => {
        if (data) {
          queryClient.setQueryData(['account', data.id], data);
          identity(data);
          dispatch(saveAccount(data));
        }
      },
      retry: (count, error) => {
        if (error.response && error.response.status === 401) {
          return false;
        }
        return count < 2;
      },
      onError: () => {
        if (!allowAnonymous) {
          logout();
        }
      },
    },
  );
};

export const useCurrentClient = (allowAnonymous?: boolean) => {
  const dispatch = useDispatch();
  const { data: account } = useMe(allowAnonymous);
  const accountId = account && account.id;
  const identity = useUpdateIdentify();
  return useQuery(
    ['accounts', accountId, 'clients'],
    queryGet(`/accounts/${accountId}/clients`),
    {
      staleTime: 1000 * 60, // 1 min
      enabled: !!accountId,
      onSuccess: (client: Client) => {
        const result = normalize(client, clientSchema);
        dispatch(getOneSuccessAction(result));
        dispatch(setCurrentClientId(client.id));
        identity(client.accountId, {
          title: client.jobTitle,
          phone: client.phoneNumber,
        });
      },
    },
  );
};

export const useReduxStore = (_store: Store) => {
  store = _store;
};
export const setReduxStore = (_store: Store) => {
  store = _store;
};

export const useCreateClient = () => {
  return useMutation<Client, AxiosError, ClientCreation>(
    queryCreate('/clients'),
  );
};

export const useCreateClientWithInvitation = (invitationId: string) => {
  return useMutation<Client, undefined, ClientWithInvitationCreation>(
    queryCreate(`/v2/organizations/invitations/${invitationId}`),
  );
};

export const useUpdateClient = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Client, AxiosError, ClientUpdate>(
    queryUpdate(`/clients/${id}`),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['accounts', data.accountId, 'clients']);
      },
    },
  );
};
